import React, { useMemo, useState } from "react";

import { MetricDef } from "consts";

import { ValidationNumericField, ValidationRow } from "models";

import { Option, Select, Table } from "components";

import { useValidationData } from "pages/admin/soilSight/hooks/useValidationData.ts";

type MetricRow = MetricDef & { isGroupHeader: false; isOdd: boolean };
type GroupHeaderRow = { key: string; label: string; isGroupHeader: true; isOdd: false };
type TableRow = MetricRow | GroupHeaderRow;

const ALL_METRIC_GROUPS: { label: string; metrics: MetricDef[] }[] = [
  {
    label: "Ecosystem Services",
    metrics: [
      { key: "ss_cropYieldFractionOfTarget", label: "Yield vs Target", unit: "score" },
      { key: "ss_yieldStability", label: "Yield Stability", unit: "score" },
      { key: "ss_soilAwcNormalised", label: "Available Water", unit: "score" },
      { key: "ss_pestLossAvoided", label: "Pest Loss Avoided", unit: "score" },
      { key: "ss_nResilience", label: "N Resilience", unit: "score" },
      { key: "ss_leaching", label: "Leaching", unit: "score" },
      { key: "ss_runoff", label: "Runoff", unit: "score" },
      { key: "ss_waterErosion", label: "Water Erosion", unit: "score" },
      { key: "ss_windErosion", label: "Wind Erosion", unit: "score" },
    ],
  },
  {
    label: "Contribution Margin (cumulative)",
    metrics: [
      { key: "cm_total", label: "CM Total", unit: "€/ha", cumulative: true },
      { key: "cm_income", label: "CM Income", unit: "€/ha", cumulative: true },
      { key: "cm_costs", label: "CM Costs", unit: "€/ha", cumulative: true },
      { key: "cm_coverCropCosts", label: "CM Cover Crop Costs", unit: "€/ha", cumulative: true },
    ],
  },
];

const LOWER_IS_BETTER = new Set(["ss_leaching", "ss_runoff", "ss_waterErosion", "ss_windErosion"]);

const formatValue = (v: number | null, decimals = 2) => (v == null ? "—" : v.toFixed(decimals));

const deltaClass = (delta: number | null, lowerIsBetter: boolean): string => {
  if (delta == null || Math.abs(delta) < 0.001) return "";
  return delta > 0 === !lowerIsBetter ? "soil-sight-detail-delta--positive" : "soil-sight-detail-delta--negative";
};

type Props = { rows: ValidationRow[] };

export const ValidationDetail = ({ rows }: Props) => {
  const { locations, rotations, scenarios } = useValidationData(rows);

  const [location, setLocation] = useState(locations[0] ?? "");
  const [rotation, setRotation] = useState(rotations[0] ?? "");

  const years = useMemo(() => {
    const ys = [...new Set(rows.map((r) => r.year))].filter(Boolean).sort((a, b) => a - b);
    return ys;
  }, [rows]);

  const baseline = scenarios.find((s) => s === "Baseline") ?? scenarios[0] ?? "";
  const comparisons = scenarios.filter((s) => s !== baseline);

  const getRow = (scenario: string, year: number) =>
    rows.find((r) => r.location === location && r.rotation === rotation && r.scenario === scenario && r.year === year);

  const cumVal = useMemo(() => {
    const result: Record<string, Record<string, Record<number, number>>> = {};
    const CM_KEYS: ValidationNumericField[] = ["cm_total", "cm_income", "cm_costs", "cm_coverCropCosts"];
    for (const scenario of scenarios) {
      result[scenario] = {};
      for (const key of CM_KEYS) {
        let running = 0;
        result[scenario][key] = {};
        for (const year of years) {
          const row = rows.find((r) => r.location === location && r.rotation === rotation && r.scenario === scenario && r.year === year);
          running += row?.[key] ?? 0;
          result[scenario][key][year] = Math.round(running);
        }
      }
    }
    return result;
  }, [rows, location, rotation, scenarios, years]);

  const getValue = (scenario: string, year: number, key: ValidationNumericField, cumulative?: boolean): number | null => {
    if (cumulative) return cumVal[scenario]?.[key]?.[year] ?? null;
    const row = getRow(scenario, year);
    return row ? row[key] ?? null : null;
  };

  const tableData: TableRow[] = useMemo(() => {
    let metricCount = 0;
    return ALL_METRIC_GROUPS.flatMap((group) => [
      { key: `group_${group.label}`, label: group.label, isGroupHeader: true as const, isOdd: false as const },
      // eslint-disable-next-line no-plusplus
      ...group.metrics.map((metric) => ({ ...metric, isGroupHeader: false as const, isOdd: metricCount++ % 2 !== 0 })),
    ]);
  }, []);

  // Total leaf columns: Metric + Unit + (baseline + comparisons) per year
  const totalLeafCols = 2 + years.length * (1 + comparisons.length);

  const columns = [
    {
      title: "Metric",
      dataIndex: "label",
      key: "metric",
      fixed: "left" as const,
      width: 160,
      render: (val: string, record: TableRow) => {
        if (record.isGroupHeader) {
          return { children: <span className="soil-sight-detail-group-label">{val}</span>, props: { colSpan: totalLeafCols } };
        }
        return val;
      },
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      fixed: "left" as const,
      width: 64,
      render: (val: string, record: TableRow) => {
        if (record.isGroupHeader) return { children: null, props: { colSpan: 0 } };
        return <span style={{ color: "#9ca3af", fontSize: "0.6875rem" }}>{val}</span>;
      },
    },
    ...years.flatMap((year) => [
      {
        title: `${baseline} — Year ${year}`,
        key: `${year}_baseline`,
        align: "right" as const,
        className: "soil-sight-detail-year-start",
        render: (_: unknown, record: TableRow) => {
          if (record.isGroupHeader) return { children: null, props: { colSpan: 0 } };
          const metricDef = record as MetricRow;
          return <span style={{ color: "#6b7280" }}>{formatValue(getValue(baseline, year, metricDef.key, metricDef.cumulative))}</span>;
        },
      },
      ...comparisons.map((scenario) => ({
        title: `${scenario} — Year ${year}`,
        key: `${year}_${scenario}`,
        align: "right" as const,
        render: (_: unknown, record: TableRow) => {
          if (record.isGroupHeader) return { children: null, props: { colSpan: 0 } };
          const metricDef = record as MetricRow;
          const baseVal = getValue(baseline, year, metricDef.key, metricDef.cumulative);
          const scVal = getValue(scenario, year, metricDef.key, metricDef.cumulative);
          const delta = scVal != null && baseVal != null ? scVal - baseVal : null;
          const lowerIsBetter = LOWER_IS_BETTER.has(metricDef.key);
          return (
            <div>
              <div>{formatValue(scVal)}</div>
              {delta != null && (
                <div className={`soil-sight-detail-delta ${deltaClass(delta, lowerIsBetter)}`}>
                  {delta > 0 ? "+" : ""}
                  {formatValue(delta)}
                </div>
              )}
            </div>
          );
        },
      })),
    ]),
  ];

  return (
    <div>
      <div className="soil-sight-validation-detail-controls">
        <Select value={location} style={{ width: 160 }} onChange={setLocation}>
          {locations.map((loc) => (
            <Option key={loc} value={loc}>
              {loc}
            </Option>
          ))}
        </Select>
        <Select value={rotation} style={{ width: 340 }} onChange={setRotation}>
          {rotations.map((rot) => (
            <Option key={rot} value={rot}>
              {rot}
            </Option>
          ))}
        </Select>
      </div>

      <Table
        data={tableData}
        columns={columns}
        size="small"
        pagination={false}
        rowKey="key"
        rowClassName={(record: TableRow) => {
          if (record.isGroupHeader) return "soil-sight-row--group-header";
          return record.isOdd ? "soil-sight-row--striped" : "";
        }}
        scroll={{ x: true }}
      />
    </div>
  );
};
